// Wishlist Adoption Banner (B3.1, Feb 2026).
//
// Passive banner rendered inside /app/settings header when the user is
// eligible AND WLP is empty. Banner is dismissed by completing the
// wishlist (server-detected) — no separate "dismiss banner" action by
// design; the modal already provides the snooze path.
//
// Clicking the CTA scrolls the page to the wishlist section. If the
// user is on a different settings tab, we still want this to "work":
// the parent MySettings page wires the `onActivate` callback to switch
// the active tab to `wishlist_preferences`.

import React from "react";
import { useLang } from "@/i18n";
import { ShieldCheck, ChevronRight } from "lucide-react";
import useWlpAdoption from "./useWlpAdoption";

export default function WishlistAdoptionBanner({ onActivate }) {
  const { t } = useLang();
  const { showBanner, disabled, emit } = useWlpAdoption();

  if (disabled || !showBanner) return null;

  const handleClick = () => {
    emit("clicked");
    if (typeof onActivate === "function") onActivate();
    // Also try anchor — covers external/cold navigation.
    const el = document.getElementById("wishlist-preferences");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-3 sm:p-4 flex items-start gap-3 shadow-sm"
      data-testid="wlp-adoption-banner"
    >
      <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-800 leading-snug">
          <span aria-hidden="true">{t("wlpAdoption.bannerSparkle")} </span>
          {t("wlpAdoption.bannerText")}
          <span className="ms-1 italic text-slate-500">{t("wlpAdoption.bannerTagline")}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 shrink-0"
        data-testid="wlp-adoption-banner-cta"
      >
        {t("wlpAdoption.bannerCta")}
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
